/**
 * Wrapper classes and custom properties for Team Member.
 *
 * Shared by edit.jsx and save.jsx so the editor preview and the saved markup
 * cannot drift — a static-save block that computes its wrapper twice is the
 * classic source of "block contains unexpected content" validation errors.
 */

import { getBlockGapValue } from '../../utils/helpers';

/**
 * Expand WordPress' preset shorthand into a usable CSS value.
 *
 * @param {string} value Attribute value.
 * @return {string} CSS value.
 */
function resolvePresetValue(value) {
  if (typeof value !== 'string' || !value.startsWith('var:')) {
    return value;
  }
  return `var(--wp--${value.slice(4).replace(/\|/g, '--')})`;
}

/**
 * @param {Object} attributes Block attributes.
 * @return {string} Wrapper class list.
 */
export function getWrapperClasses(attributes) {
  const { layout, contentAlign, hoverEffect } = attributes;

  return [
    'brandy-team-member',
    `is-layout-${layout}`,
    `is-aligned-${contentAlign}`,
    hoverEffect && hoverEffect !== 'none' ? `has-hover-${hoverEffect}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Avatar variables are set here rather than passed only through block context:
 * context reaches the avatar's own markup, but the surrounding layout (the
 * overlay scrim height, the horizontal column width) also needs the size.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} React style object.
 */
export function getWrapperStyles(attributes) {
  const { avatarSize, avatarRingColor, avatarRingWidth, style } = attributes;

  const styles = {
    '--brandy-avatar-size': `${avatarSize}px`,
    '--brandy-avatar-ring-width': `${avatarRingWidth}px`,
  };

  if (avatarRingColor) {
    styles['--brandy-avatar-ring-color'] = resolvePresetValue(avatarRingColor);
  }

  /*
   * Core only ever writes blockGap through the layout support, and this block
   * declares none — the stacked column and the horizontal grid are its own.
   * Declaring the support without serializing it left the Dimensions control
   * writing an attribute nothing read, so the card kept the theme's spacing.
   * `__experimentalSkipSerialization` in block.json states that this file owns
   * the value. Left unset when empty so the stylesheet's fallback chain
   * decides, rather than pinning the global gap into every wrapper.
   */
  const gap = getBlockGapValue(style?.spacing?.blockGap);

  if (gap) {
    styles['--brandy-team-member-gap'] = gap;
  }

  return styles;
}
